/**
 * Broker Integration Page (Web)
 * Handles OAuth callback results from backend after broker authorization
 * 
 * This page receives redirects from the backend broker-integration-service
 * after OAuth callbacks are processed. It displays success/error messages
 * and redirects users to the appropriate page.
 * 
 * Query params expected:
 * - success=true&message=...&broker=... (on success)
 * - error=true&message=...&broker=... (on error)
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { useBrokerAccounts } from '@shared/features/broker-accounts';

export function BrokerIntegrationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loadAccounts } = useBrokerAccounts();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState<string | null>(null);
  const [broker, setBroker] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Extract parameters from URL
        const success = searchParams.get('success');
        const error = searchParams.get('error');
        const messageParam = searchParams.get('message');
        const brokerParam = searchParams.get('broker');

        // Clean up URL parameters
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('success');
        newUrl.searchParams.delete('error');
        newUrl.searchParams.delete('message');
        newUrl.searchParams.delete('broker');
        newUrl.searchParams.delete('registration_id');
        window.history.replaceState({}, '', newUrl);

        if (success === 'true') {
          const successMessage = messageParam 
            ? decodeURIComponent(messageParam) 
            : 'Broker account connected successfully';
          const brokerName = brokerParam 
            ? brokerParam.charAt(0).toUpperCase() + brokerParam.slice(1) 
            : 'Broker';
          
          setStatus('success');
          setMessage(successMessage);
          setBroker(brokerName);
          
          // Refresh accounts list
          try {
            await loadAccounts();
          } catch (err) {
            console.error('[BrokerIntegrationPage] Failed to refresh accounts:', err);
            // Continue anyway - we still show success message
          }
        } else if (error === 'true') {
          const errorMessage = messageParam 
            ? decodeURIComponent(messageParam) 
            : 'OAuth callback failed';
          const brokerName = brokerParam 
            ? brokerParam.charAt(0).toUpperCase() + brokerParam.slice(1) 
            : 'Broker';
          
          setStatus('error');
          setMessage(errorMessage);
          setBroker(brokerName);
        } else {
          // No success/error params - redirect to broker accounts
          navigate('/broker-accounts');
        }
      } catch (error: unknown) {
        console.error('[BrokerIntegrationPage] Error processing callback:', error);
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Failed to process OAuth callback');
      }
    };

    processCallback();
  }, [searchParams, loadAccounts, navigate]);

  const handleDialogClose = () => {
    if (status === 'success') {
      navigate('/broker-accounts');
    } else if (status === 'error') {
      navigate('/broker-accounts');
    }
  };

  const handleTryAgain = () => {
    navigate('/broker-accounts/add');
  };

  return (
    <AuthenticatedLayout>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center',
          px: 2,
        }}
      >
        {status === 'processing' && (
          <>
            <CircularProgress sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              Processing broker authorization...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we complete the account linking process.
            </Typography>
          </>
        )}

        {/* Success Dialog */}
        <Dialog
          open={status === 'success'}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={false}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
            <Typography variant="h6">
              {broker} Connected Successfully!
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="contained"
              onClick={handleDialogClose}
              fullWidth
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Error Dialog */}
        <Dialog
          open={status === 'error'}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={false}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: 'error.main', fontSize: 28 }} />
            <Typography variant="h6">
              Connection Failed
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleDialogClose}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleTryAgain}
            >
              Try Again
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AuthenticatedLayout>
  );
}

